/**
 * ListViewHandler is a module in charge of rendering results in the grid where cards are shown to the user.
 */
import { store, store$ } from '@redux/store';
import { selectResultsPresentationType } from 'layout/redux/selectors';
import {
  searchParametersActions,
  selectSearchModifiers,
  getResults$,
  selectSearchParameters,
  selectResultType,
  ResultType,
} from 'features/searchResultsFacets';
import { catchError, distinctUntilChanged, filter, map, of, shareReplay, switchMap } from 'rxjs';
import ResultPresentationType from 'layout/constants/resultPresentationType';
import fastdom from 'fastdom';
import LoadingStatus from 'enums/loadingStatus';
import trackEvents from './eventTracking/totalDeliveryValue';
import onListViewPaint from '../redux/actions';
import hiddeHeaderSection from './noResults/handlerNoResultsSection';
import { selectResultsLoadedAndResultsPresentationType, selectSelectedMarker } from '../redux/selectors';

/**
 * loadResults: inserts the more recent loaded results in the grid view.
 */
const loadResults = ({ presentationType, results }) => {
  const element = document.querySelector('[data-list-view-element]');

  if (presentationType === ResultPresentationType.InfiniteScrolling.toString()) {
    const result = document.createRange().createContextualFragment(results);
    element.appendChild(result);
  } else {
    element.innerHTML = results;
  }

  store.dispatch(onListViewPaint());
};

/**
 * loadingResult$ observes and emits a value every time we are loading results.
 */
const loadingResult$ = store$.pipe(
  filter(
    (state) =>
      selectResultType(state) !== ResultType.Communities.toString() ||
      selectSelectedMarker(state)?.marker === null ||
      selectSelectedMarker(state)?.marker === undefined
  ),
  map(selectResultsLoadedAndResultsPresentationType),
  distinctUntilChanged(),
  filter((information) => information.fetchListingStatus?.name === LoadingStatus.Loading.toString()),
  filter((information) => information.presentationType !== ResultPresentationType.InfiniteScrolling.toString())
);

loadingResult$.subscribe(() =>
  fastdom.mutate(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  })
);

/**
 * onLoadingResults$ observes and emits a value to determine when we have to trigger the ajax request in charge of
 * bringing new results based in the users' search criteria.
 */
const onLoadingResults$ = store$.pipe(
  map(selectSearchModifiers),
  distinctUntilChanged(),
  filter(({ fetchResultsListingStatus }) => fetchResultsListingStatus?.name === LoadingStatus.Loading.toString())
);

/**
 * onResultLoad$ observes and emits a value when new results are ready from server side.
 */
const onResultLoad$ = onLoadingResults$.pipe(
  switchMap(() => {
    const state = store.getState();
    const payload = {
      searchParameters: selectSearchParameters(state),
      selectedMarkerId: selectSelectedMarker(state)?.marker?.id,
    };
    const presentationType = selectResultsPresentationType(state);
    const response = {
      presentationType,
    };

    return getResults$(payload).pipe(
      switchMap((data) => {
        response.results = data;

        loadResults(response);
        store.dispatch(searchParametersActions.fetchResultsListingFulfilled());

        return of(response);
      }),
      catchError((error) => {
        response.results = error;
        store.dispatch(searchParametersActions.fetchResultsListingError());

        return of(error);
      })
    );
  }),
  shareReplay(1)
);

onResultLoad$.subscribe(hiddeHeaderSection);
onResultLoad$.subscribe(trackEvents);
